import React, { useEffect, useRef, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../../../components/BreadCrumbs';
import StyledHeadingText from '../../../components/Fonts/StyledHeadingText';
import InputField from '../../../components/inputFields/InputField';
import { Form, Formik } from 'formik';
import Button from '../../../components/buttons/Button';
import { hoThyroidOptions } from '../../../constants/h_o_thyroid_options';
import FormikDropdown from '../../../components/inputFields/FormikDropDown';
import FormikMultiSelectDropdown from '../../../components/inputFields/FormikMultiSelectDropdown';
import { toast } from 'react-toastify';
import DoseTable from '../../../components/Hud/DoseTable';
import { formatDateTime } from '../../../utils/FormatDateTime';
import CustomLoading from '../../../components/customLoading/CustomLoading';
import ShowModal from '../../../components/Hud/ShowModal';
import LineChart from '../../../components/LineChart';
import partLogo from '../../../assets/icons/part-logo.png';
import { pricesColorCoding } from '../../../constants/prices_colors_coding';
import NewPatientMedicineView from '../patients/new_patient_medicine_view';
import NewPatientPackageComment from '../patients/new_patient_package_comment';
import { getNewHudOintments, getNewHudTablets, postNewRating, postNewHudMedicineChanges, addNewHudPatientPackageComment, updateNewHudPatientDoctorView, patchNewHudPatientsHistory, addNewHudPatientPackage } from '../../../services/new_clinic/new_hud_patients';
import { getClinicDoctorView } from '../../../services/new_clinic/clinic_doctor_view';
import { useLocation } from 'react-router-dom';
import NewPatientDoctorPdfView from '../patients/new_patient_doctor_pdf_view';
import NewAddUpdateDose from '../patients/new_add_update_dose';
import PatientPhotoGalleryApiComponent from '../../../components/PatientPhotoGalleryApiComponent';
import { newHudPatientApiCRUDUrl } from '../../../constants/apis/new_hud_apis';
import AsyncFormikDropdown from '../../../components/inputFields/AsyncFormikDropDown';
import { getUserRole } from '../../../utils/localStoarege';
import { searchPatientApi } from '../../../services/new_clinic/patient_search';

// Developer-only: add any package type / amount with no remaining-amount or color gating.
const DevAddPackageForm = ({ patientId, onAdded }) => {
    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            const response = await addNewHudPatientPackage(patientId, values);
            if (response?.status === 201 || response?.status === 200) {
                toast.success("Package added successfully!");
                onAdded();
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            toast.error(error?.response?.data?.error || "Oops! Something went wrong, please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="w-full flex-1">
            <StyledHeadingText text="Add Package (Developer)" />
            <p className="text-xs text-red-600 mb-2">
                Developer override — no remaining-amount or package-color restrictions are applied.
            </p>
            <Formik
                initialValues={{ package_type: "TPT", package_status: "Paid", amount: "" }}
                validationSchema={Yup.object({
                    package_type: Yup.string().required("Package is required"),
                    package_status: Yup.string().required("Type is required"),
                    amount: Yup.number().typeError("Amount must be a number").required("Amount is required"),
                })}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, setFieldValue, values }) => (
                    <Form className="space-y-2 my-4">
                        <div className="flex flex-col">
                            <FormikDropdown
                                label="Package Type"
                                name="package_type"
                                placeholder="Select Package"
                                required
                                options={[
                                    { label: "TPT", value: "TPT" },
                                    { label: "HPF", value: "HPF" },
                                    { label: "FULL BODY", value: "FULL BODY" },
                                    { label: "HAND", value: "HAND" },
                                    { label: "FEET", value: "FEET" },
                                    { label: "HAND FEET", value: "HAND FEET" },
                                ]}
                            />
                            <FormikDropdown
                                label="Package Status"
                                name="package_status"
                                placeholder="Select Type"
                                required
                                options={[
                                    { label: "Paid", value: "Paid" },
                                    { label: "FOC", value: "FOC" },
                                ]}
                                onChange={(value) => {
                                    setFieldValue("amount", value.value === "FOC" ? 0 : "");
                                }}
                            />
                            <InputField
                                label="Amount"
                                name="amount"
                                type="number"
                                placeholder="Enter any amount"
                                required
                                disable={values.package_status === "FOC"}
                            />
                            <Button
                                type="submit"
                                className="bg-primary text-white px-4 py-2 rounded max-w-32"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Adding..." : "Add"}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

function ClinicDoctorView() {
    const location = useLocation();
    const timerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [loadingBg, setLoadingBg] = useState(false);
    const [availableTablets, setAvailableTablets] = useState([]);
    const [availableOintments, setAvailableOintments] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [rowData, setRowData] = useState(null);
    const [initialValues, setInitialValues] = useState({
        "patient_id": "",
        "full_name": "",
        "address": "",
        "dob": "",
        "age": "",
        "date": "",
        "gender": "",
        "phone_number": "",
        "system_code": "",
        "skin_type": "",
        "registration_date": "",
        "patient_history": {
            "patient_history_id": "",
            "rating": "",
            "duration": "",
            "activity": "",
            "auto_imm_dis": "",
            "thyroid": "",
            "jaundice": "",
            "fh_auto_imm_dis": "",
            "ophthalmological_problems": "",
            "hospitalization": "",
            "psychiatric_history": "",
            "keloidol_tendency": "",
            "epilepsy": "",
            "blood_group": "",
            "steroid_history": "",
            "antibodies_to_thyroid": "",
            "photo_of_date": "",
            "h_o_epilepsy": "",
            "fh_b_vitiligo": "",
            "h_o_thyroid": "",
            "hb_value": "",
            "tablets": [],
            "ointments": [],
        },
        "packages": [],
        "photo": null
    });

    const patientIdRef = useRef(initialValues.patient_id);

    const handlePatientChange = async (value) => {
        setLoading(true);
        console.log(value);

        try {
            let res = await updateNewHudPatientDoctorView(rowData, value);
            if (res.status === 200) {
                fetchPatientDetails(true, rowData);
                toast.success(`Patient activated on doctor's view`);
            } else {
                toast.error("Unable to change deoctor view");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error updating doctor view:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const center = location.state;
        if (center) {
            setRowData(center);
        } else {
            setRowData(null);
        }

        // Start the timer automatically when the component mounts
        startTimer(center);

        // Stop the timer automatically when the component unmounts or the user leaves the page
        return () => {
            stopTimer();
        };
    }, [location.state]);

    const fetchPatientDetails = async (needLoading = true, center = null) => {
        if (needLoading) {
            setLoading(true);
        } else {
            setLoadingBg(true);
        }
        try {
            let res = await getClinicDoctorView(center);
            if (res.status === 200) {
                // Use ref to compare the current patient_id
                // if (res.data.id !== patientIdRef.current || needBypass) {
                setInitialValues(res.data); // Update state
                patientIdRef.current = res.data.id; // Update ref
                // }
                setLoading(false);
                setLoadingBg(false);
            } else {
                toast.error("Unable to fetch patient");
            }
        } catch (error) {
            console.error("Unable to fetch patient details:", error);
        }
    };

    const fetchTablets = async () => {
        try {
            let res = await getNewHudTablets();
            if (res.status === 200) {
                setAvailableTablets(res.data);
            } else {
                toast.error("Unable to fetch Medicines");
            }
        } catch (error) {
            console.error("Unable to fetch Medicines:", error);
        }
    };

    const fetchOintments = async () => {
        try {
            let res = await getNewHudOintments();
            if (res.status === 200) {
                setAvailableOintments(res.data);
            } else {
                toast.error("Unable to fetch Medicines");
            }
        } catch (error) {
            console.error("Unable to fetch Medicines:", error);
        }
    };

    const handleSubmitRating = async (values, { setSubmitting, resetForm }) => {
        try {
            const updatedRatings = parseInt(values.rating);

            const response = await postNewRating(initialValues.id, {
                patient_id: initialValues.id,
                rating: updatedRatings,
            });

            if (response?.status === 201) {
                toast.success("Rating added successfully!");
                fetchPatientDetails();
                resetForm();
            } else {
                let errors = "Oops! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };


    const handleSubmitComment = async (packageId, values, { setSubmitting, resetForm }) => {
        try {
            const response = await addNewHudPatientPackageComment(packageId, values);
            if (response?.status === 201) {
                fetchPatientDetails(false);
                // resetForm();
                toast.success("Comment added successfully!");
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            let errorMessage =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const [submitting, submittingUpdate] = useState(false);

    const handleMedicinesUpdate = async (values) => {
        submittingUpdate(true);
        handleHistoryUpdate(values);
        try {
            const response = await postNewHudMedicineChanges({
                medicines: [...values.tablets, ...values.ointments],
            }, initialValues.id);

            if (response?.status === 200) {
                toast.success("Medicines updated successfully!");
            } else {
                let errors = "Oops! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        }
        submittingUpdate(false);
    };

    const handleHistoryUpdate = async (values) => {
        try {
            const response = await patchNewHudPatientsHistory(initialValues.id, { antibodies_thyroid: values.antibodies_to_thyroid, ho_epilepsy: values.h_o_epilepsy, ho_thyroid: values.h_o_thyroid });

            if (response?.status === 200) {
                toast.success("History updated successfully!");
            } else {
                let errors = "Oops! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        }
    };

    // Function to start the timer automatically
    const startTimer = (center) => {
        if (timerRef.current) {
            clearInterval(timerRef.current); // Clear existing timer, if any
            timerRef.current = null;
        }
        fetchPatientDetails(true, center); // Initial fetch
        timerRef.current = setInterval(() => {
            fetchPatientDetails(false, center); // Periodic fetch
        }, 10000); // 10 seconds
    };

    // Function to stop the timer
    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current); // Clear the interval
            timerRef.current = null;
        }
    };

    useEffect(() => {
        fetchTablets();
        fetchOintments();
    }, []); // Empty dependency array ensures this runs only on mount and unmount

    const [isPackageCommentModalOpen, setIsPackageCommentModalOpen] = useState(false);
    const [openingPackage, setOpeningPackage] = useState({});

    const [isAddDoseModalOpen, setIsAddDoseModalOpen] = useState(false);
    const [addDosePackage, setAddDosePackage] = useState({});

    const openAddDoseModal = (item) => {
        setAddDosePackage(item);
        setIsAddDoseModalOpen(true);
    };

    const closeAddDoseModal = () => {
        setIsAddDoseModalOpen(false);
        setAddDosePackage({});
    };

    const [isAddPackageModalOpen, setIsAddPackageModalOpen] = useState(false);

    const [isRatingChartModalOpen, setIsRatingChartModalOpen] = useState(false);
    const [isMedicineModalOpen, setIsMedicineModalOpen] = useState(false);
    const [medicineModalOpenFor, setIsMedicineModalOpenFor] = useState("");
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

    // Open modal with the selected PDF ID
    const openPackageCommentModal = (item) => {
        setOpeningPackage(item);
        setIsPackageCommentModalOpen(true);
    };

    const openRatingChartModal = (item) => {
        setIsRatingChartModalOpen(item);
    };

    const openPdfModal = (item) => {
        setIsPdfModalOpen(true);
    };

    const closePdfModal = () => {
        setIsPdfModalOpen(false);
    }

    // Close modal
    const closePackageCommentModal = () => {
        setIsPackageCommentModalOpen(false);
        setOpeningPackage({});
    };

    const openMedicineModal = (modelFor) => {
        setIsMedicineModalOpen(true);
        setIsMedicineModalOpenFor(modelFor);
    };

    // Close modal
    const closeMedicineModal = () => {
        setIsMedicineModalOpen(false);
        setIsMedicineModalOpenFor("");
    };

    const closeRatingGraphModal = () => {
        setIsRatingChartModalOpen(false);
    };

    const handleCommentAdded = () => {
        closePackageCommentModal();
        // fetchPatientDetails(false, true);
    }

    return (loading ? <CustomLoading /> : <>
        <div className="flex flex-row justify-between text-md">
            <Breadcrumbs items={[
                { name: 'Home', link: "/doctor/home" },
                { name: 'Doctor\'s View' },
            ]} />
            {
                loadingBg ? <p className="text-primary font-bold">Syncing...</p> : <></>
            }
        </div>
        <div className="flex flex-row justify-between items-center my-2">
            <div className="flex flex-row gap-2">
                <StyledHeadingText size={"text-[1.2rem] font-bold"} text={"Patient Details: "} />
                <StyledHeadingText size={"text-[1.2rem] font-bold"} TextColor="text-red-600" text={initialValues.full_name} />
                <StyledHeadingText size={"text-[1.2rem] font-bold"} text={" | "} />
                <StyledHeadingText size={"text-[1.2rem] font-bold"} text={"Registration Date: "} />
                <StyledHeadingText size={"text-[1.2rem] font-bold"} TextColor="text-red-600" text={formatDateTime(initialValues.registration_date)} />
            </div>
            {
                getUserRole() !== "operator" ?
                    <Formik
                        initialValues={{
                            patient: ''
                        }}
                        onSubmit={(value) => { }}
                        enableReinitialize
                    >
                        {({ setFieldValue }) => (
                            <Form className='min-w-64'>
                                <AsyncFormikDropdown
                                    label=""
                                    name="patient"
                                    placeholder="Select Patient"
                                    apiCallFunction={async (input) => {
                                        const response = await searchPatientApi(rowData, input);
                                        setUserOptions(response.data);
                                        return response.data.map((patient) => ({
                                            value: patient.id,
                                            label: `${patient.patient_name} | ${patient.patient_phone}`,
                                        }));
                                    }}
                                    required
                                    needSearch={true}
                                    onChange={(selectedOption) => {
                                        setFieldValue('patient', selectedOption);
                                        const selectedPatient = userOptions.find(
                                            (item) => item.id === selectedOption.value
                                        );

                                        if (selectedPatient) {
                                            handlePatientChange(selectedPatient.patient_id);
                                        }
                                    }}
                                />
                            </Form>
                        )}
                    </Formik> : <></>
            }
        </div>
        <Formik
            initialValues={{
                ...initialValues,
                antibodies_to_thyroid: initialValues.patient_history.antibodies_to_thyroid,
                photo_of_date: initialValues.patient_history.photo_of_date,
                h_o_epilepsy: initialValues.patient_history.h_o_epilepsy,
                h_o_thyroid: initialValues.patient_history.h_o_thyroid,
                tablets: initialValues.patient_history.tablets,
                ointments: initialValues.patient_history.ointments,
                rating: "",
                age_gen: `${initialValues.age} | ${initialValues.gender}`
            }}
            validationSchema={Yup.object({
                rating: Yup.number()
                    .required("Rating is required")
                    .min(-5, "Rating must be between -5 and 5")
                    .max(5, "Rating must be between -5 and 5"),
            })}
            onSubmit={handleSubmitRating}
            enableReinitialize
        >
            {({ isSubmitting, setFieldValue, resetForm, values }) => (
                <Form>
                    <div className="flex flex-col lg:flex-row gap-4 w-full">
                        <div className="border rounded-md shadow-lg p-2 border-primary bg-white lg:max-w-min">
                            <div className="flex flex-row justify-between">
                                <PatientPhotoGalleryApiComponent imageToDisplay={initialValues.photo || partLogo} className='rounded-full aspect-square max-w-16 border border-primary' apiEndpoint={newHudPatientApiCRUDUrl + initialValues.id + "/photos/?page_size=20"} />
                                <InputField
                                    label="Age | Gen"
                                    name="age_gen"
                                    type="text"
                                    placeholder="XX"
                                    disable
                                />
                            </div>
                            <InputField
                                label="Address"
                                name="address"
                                type="text"
                                placeholder="User Address"
                                disable
                            />
                            <div className="flex flex-row gap-2">
                                <InputField
                                    label="Skin Type"
                                    name="skin_type"
                                    type="text"
                                    placeholder="V"
                                    disable
                                />
                                <InputField
                                    label="Phone Number"
                                    name="phone_number"
                                    type="text"
                                    placeholder="XXXXXXXXX"
                                    disable
                                />
                            </div>
                            <InputField
                                label="System Code"
                                name="system_code"
                                type="text"
                                placeholder="XXXXXXXXX"
                                disable
                            />
                            <div className="flex flex-row items-end">
                                <div className="flex-1">
                                    <InputField
                                        label="Rating"
                                        name="rating"
                                        type="number"
                                        placeholder="4"
                                        required
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="text-white bg-secondary hover:bg-primary-dark font-sm text-md px-4 py-2.5 text-center max-h-12 mb-1 mr-1"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Adding..." : "Add"}
                                </Button>
                                <div
                                    className="rounded text-white bg-primary hover:bg-primary-dark font-sm text-md px-4 py-2.5 text-center max-h-12 mb-1 cursor-pointer"
                                    onClick={() => openRatingChartModal(true)}
                                >
                                    View
                                </div>
                            </div>
                            <div className="flex flex-row gap-2 mt-1">
                                <div
                                    className="rounded text-white bg-primary hover:bg-primary-dark font-sm text-md px-8 py-2.5 text-center max-h-12 mb-1 cursor-pointer"
                                    onClick={() => openPdfModal(true)}
                                >
                                    PDFs
                                </div>
                                <PatientPhotoGalleryApiComponent isOperator={false} componentToDisplay={<div
                                    className="rounded text-white bg-primary hover:bg-primary-dark font-sm text-md px-8 py-2.5 text-center max-h-12 mb-1 cursor-pointer"
                                >
                                    Approvals
                                </div>} className='rounded-full aspect-square max-w-16 border border-primary' apiEndpoint={newHudPatientApiCRUDUrl + initialValues.id + "/approval-images/"} />
                            </div>
                        </div>
                        <div className="border rounded-md shadow-lg p-2 border-primary flex-1 bg-white">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-0.5 mt-2">
                                {[
                                    { label: "Duration", value: initialValues.patient_history.duration, },
                                    { label: "Activity (koebnerisation)", value: initialValues.patient_history.activity, },
                                    { label: "H/O auto immune diseases", value: initialValues.patient_history.auto_imm_dis, },
                                    { label: "Thyroid", value: initialValues.patient_history.thyroid, },
                                    { label: "H/O Jaundice", value: initialValues.patient_history.jaundice, },
                                    { label: "F/H (A) auto immune diseases", value: initialValues.patient_history.fh_auto_imm_dis, },
                                    { label: "F/H (B) vitiligo", value: initialValues.patient_history.fh_b_vitiligo, },
                                    { label: "Ophthalmological problems", value: initialValues.patient_history.ophthalmological_problems, },
                                    { label: "H/O hosptalisathion", value: initialValues.patient_history.hospitalization, },
                                    { label: "Psychiatric history", value: initialValues.patient_history.psychiatric_history, },
                                    { label: "Keloidol tendencey", value: initialValues.patient_history.keloidol_tendency, },
                                    { label: "Epilepsy", value: initialValues.patient_history.epilepsy, },
                                    { label: "Blood Group", value: initialValues.patient_history.blood_group, },
                                    { label: "H. B.", value: initialValues.patient_history.hb_value, },
                                    { label: "History of steroid", value: initialValues.patient_history.steroid_history, },
                                ].map((card, index) => (
                                    <div className="flex flex-col">
                                        <div className="flex flex-row justify-center items-center bg-primary opacity-55 text-[0.7rem] text-center text-white py-1 px-1 h-10">{card.label}</div>
                                        <div className="flex flex-row justify-center items-center text-red-500 font-extrabold text-center py-1 text-[0.7rem] px-1 h-10 border border-secondary">{card.value}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="flex flex-row items-end">
                                    <div className="flex-1">
                                        <InputField
                                            label="Photo Date"
                                            name="photo_of_date"
                                            type="date"
                                            placeholder="V"
                                            disable
                                        />
                                    </div>
                                    <div
                                        className="text-white bg-primary hover:bg-primary-dark font-sm text-md px-8 py-2.5 text-center max-h-12 mb-2 rounded cursor-pointer"
                                        onClick={() => openMedicineModal("photo")}
                                    >
                                        View
                                    </div>
                                </div>
                                <InputField
                                    label="Antibodies to Thyroid"
                                    name="antibodies_to_thyroid"
                                    placeholder="Antibodies to Thyroid"
                                />
                                <FormikDropdown
                                    label="H/O Epilepsy"
                                    name="h_o_epilepsy"
                                    placeholder="Select"
                                    options={[
                                        { value: "YES", label: "Yes" },
                                        { value: "NO", label: "No" },
                                    ]}
                                />
                            </div>
                            <div
                                className="grid grid-cols-1 lg:grid-cols-2 gap-4"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        if (!submitting) {
                                            handleMedicinesUpdate(values);
                                        }
                                    }
                                }}
                            >
                                <div className="flex flex-row items-end">
                                    <div className="flex-1">
                                        <FormikMultiSelectDropdown
                                            label="Tablets"
                                            name="tablets"
                                            needSearch
                                            placeholder="Select Tablets"
                                            options={availableTablets.map((item) => ({
                                                value: item.medicine_id,
                                                label: item.name,
                                            }))}
                                        />
                                    </div>
                                    <div
                                        className="text-white bg-primary hover:bg-primary-dark font-sm text-md px-8 py-2.5 text-center max-h-12 mb-2 rounded cursor-pointer"
                                        onClick={() => openMedicineModal("tablet")}
                                    >
                                        View
                                    </div>
                                </div>
                                <div className="flex flex-row items-end">
                                    <div className="flex-1">
                                        <FormikMultiSelectDropdown
                                            label="Ointments"
                                            name="ointments"
                                            needSearch
                                            placeholder="Select Ointments"
                                            options={availableOintments.map((item) => ({
                                                value: item.medicine_id,
                                                label: item.name,
                                            }))}
                                        />
                                    </div>
                                    <div
                                        className="text-white bg-primary hover:bg-primary-dark font-sm text-md px-8 py-2.5 text-center max-h-12 mb-2 rounded cursor-pointer"
                                        onClick={() => openMedicineModal("ointment")}
                                    >
                                        View
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row items-end">
                                <div className="flex-1">
                                    <FormikDropdown
                                        label="H/O Thyroid"
                                        name="h_o_thyroid"
                                        placeholder={initialValues.patient_history.h_o_thyroid || "Select"}
                                        needSearch
                                        options={hoThyroidOptions.map((item) => ({
                                            value: item,
                                            label: item,
                                        }))}
                                    />
                                </div>
                                <div
                                    type="submit"
                                    className="text-white bg-secondary hover:bg-primary-dark font-sm text-md px-8 py-3 cursor-pointer text-center max-h-12 mb-2 mr-1 rounded"
                                    onClick={submitting ? null : () => handleMedicinesUpdate(values)}
                                >
                                    {submitting ? "Updating..." : "Update"}
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik >
        <div className="py-2">
            {getUserRole() === "developer" && (
                <div className="flex flex-row justify-end mb-2">
                    <Button
                        className="text-white bg-secondary hover:bg-primary-dark text-xs px-4 py-2.5"
                        onClick={() => setIsAddPackageModalOpen(true)}
                    >
                        + Add Package (Dev)
                    </Button>
                </div>
            )}
            {initialValues.packages.length > 0 ? initialValues.packages.map((data, index) => (
                <div className="flex flex-col border-y-2 border-primary mb-1 bg-white shadow-lg px-3 pb-2 overflow-x-auto">
                    <div className="flex flex-row pt-2 justify-start items-end gap-2 mb-2">
                        <div className="border rounded-md bg-gray-100 px-2 mb-0.5 py-2.5 text-sm flex flex-row items-center justify-start space-x-2 min-w-80">
                            <p className='text-red-600 font-bold'>{data.package_name}</p>
                            {/* <span> | Rem. Am.- {data.remaining_amount ? "₹ " + (data.remaining_amount / 1000) : ""}</span> */}
                            <p className="font-bold text-md">Pkg. Color: {pricesColorCoding.find((e) => e.amount === data.amount)?.title ?? `₹${data.amount}`}</p>
                            <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === data.amount)?.bg ?? ""} ${pricesColorCoding.find((e) => e.amount === data.amount)?.color ? "" : "hidden"}`}></span>
                            <p className="font-bold text-md">{pricesColorCoding.find((e) => e.amount === data.amount)?.short_hand ?? ""}</p>
                            <span className={`font-bold uppercase ${data.package_status === "paid" ? "text-primary" : ""}`}> | {data.package_status}</span>
                            {/* <Button className={`${data.amount === 10000 && data.package_name.toUpperCase() === "FULL BODY" ? "hidden" : ""} ${data.approval_image ? "bg-green-500" : "bg-red-700"} rounded-full text-white text-md`} onClick={data.approval_image ? () => { window.open(data.approval_image, "_blank"); } : null}>{data.approval_image ? <BiCheckCircle color="white" size={14} /> : <BiWindowClose color="white" size={14} />}</Button> */}
                        </div>

                        {getUserRole() === "developer" && (
                            <div className="border rounded-md bg-gray-100 px-3 mb-0.5 py-2.5 text-sm flex flex-row items-center gap-3 whitespace-nowrap">
                                <span className="font-bold">Amount: ₹{Number(data.amount || 0).toLocaleString("en-IN")}</span>
                                <span className="font-bold text-green-600">Paid: ₹{Number(data.paid_amount || 0).toLocaleString("en-IN")}</span>
                                <span className="font-bold text-red-600">Remaining: ₹{Number(data.remaining_amount ?? ((data.amount || 0) - (data.paid_amount || 0))).toLocaleString("en-IN")}</span>
                            </div>
                        )}

                        {/* <div className="border rounded-md bg-gray-100 px-2 items-center justify-center py-1.5 text-md">
                            Package- <span className='text-red-600 font-bold'>{data.package_name}</span> | Date- {data.date.includes(" ") ? data.date.split(" ")[0] : data.date} | Amount- {data.amount ? "₹ " + (data.amount / 1000) : ""} | Rem. Am.- {data.remaining_amount ? "₹ " + (data.remaining_amount / 1000) : ""}
                        </div> */}
                        <Formik
                            initialValues={{
                                comment: data.last_package_comment ?? "",
                            }}
                            validationSchema={Yup.object({
                                comment: Yup.string().required("Please enter a comment"),
                            })}
                            onSubmit={(values, { setSubmitting, resetForm }) =>
                                handleSubmitComment(data.id, values, { setSubmitting, resetForm })
                            }
                            enableReinitialize
                            className="w-full"
                        >
                            {({ isSubmitting }) => (
                                <Form className='w-full'>
                                    <div className="flex flex-row items-end justify-end">
                                        <InputField
                                            name="comment"
                                            type="text"
                                            key={index}
                                            placeholder={"Write commente here..."}
                                            className={"text-red-600 flex-1 rounded font-bold text-md"}
                                        />
                                        <Button
                                            type="submit"
                                            className="text-white bg-secondary hover:bg-primary-dark font-sm text-md px-8 py-2.5 text-center max-h-12 mb-1 mr-0.5"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? "Adding..." : "Add"}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                        <Button
                            className="text-white bg-primary hover:bg-primary-dark font-sm text-md px-8 py-2.5 text-center max-h-12 mb-1"
                            onClick={() => openPackageCommentModal(data)}
                        >
                            View
                        </Button>
                        <Button
                            className="text-white bg-secondary hover:bg-primary-dark text-xs px-3 py-2.5 text-center max-h-12 mb-1 whitespace-nowrap"
                            onClick={() => openAddDoseModal(data)}
                        >
                            Add Dose
                        </Button>
                    </div>
                    {
                        <DoseTable
                            data={data.doses}
                            packageId={data.package_id}
                            fetchData={() => fetchPatientDetails()}
                            needExtraOptions={false}
                            isNewClinic={true}
                        />
                    }
                    <div className="flex flex-row justify-center items-center mt-2 gap-2">
                        <StyledHeadingText size={"text-sm font-bold"} TextColor="text-red-600" text="Adgain Date:" />
                        <StyledHeadingText size={"text-sm font-bold"} text={data.payment_completion_date || "N/A"} />
                    </div>
                </div>
            )) : <div className='flex flex-row justify-center border-y-2 border-primary my-4 bg-white shadow-lg px-3 pb-2 overflow-x-auto'>No Packages Found</div>}
        </div>
        <ShowModal modalWidth={"50%"} contentLabel={"Add Dose alert"} isOpen={isAddDoseModalOpen} onRequestClose={closeAddDoseModal} componentToPass={<NewAddUpdateDose packageId={addDosePackage.id} packageName={addDosePackage.package_name} onDoseOperation={() => { fetchPatientDetails(false); closeAddDoseModal(); }} />} />
        <ShowModal modalWidth={"40%"} contentLabel={"Add Package alert"} isOpen={isAddPackageModalOpen} onRequestClose={() => setIsAddPackageModalOpen(false)} componentToPass={<DevAddPackageForm patientId={initialValues.id} onAdded={() => { fetchPatientDetails(false); setIsAddPackageModalOpen(false); }} />} />
        <ShowModal modalWidth={"80%"} contentLabel={"Package Comment alert"} isOpen={isPackageCommentModalOpen} onRequestClose={closePackageCommentModal} componentToPass={<NewPatientPackageComment packageId={openingPackage.id} onAddNewComment={handleCommentAdded} packageComments={openingPackage.package_comments} />} />
        <ShowModal modalWidth={"50%"} contentLabel={"Ratings alert"} isOpen={isRatingChartModalOpen} onRequestClose={closeRatingGraphModal} componentToPass={<LineChart label={'Ratings'} dataPoints={(initialValues.patient_history.rating ?? "").length !== 0 ? JSON.parse(initialValues.patient_history.rating) : []} />} />
        <ShowModal modalWidth={"50%"} contentLabel={"View Medcine alert"} isOpen={isMedicineModalOpen} onRequestClose={closeMedicineModal} componentToPass={<NewPatientMedicineView openedFor={medicineModalOpenFor} patientId={initialValues.id} />} />
        <ShowModal modalWidth={"90%"} contentLabel={"View PDF alert"} isOpen={isPdfModalOpen} onRequestClose={closePdfModal} componentToPass={<NewPatientDoctorPdfView patientId={initialValues.id} />} />
    </>);
}

export default ClinicDoctorView;